import { ArrowLeft, Calendar, Clock, User, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Link } from "react-router-dom";
import Footer from "@/components/Footer";
import BlogPostImage from "@/components/BlogPostImage";

const BlogPost4 = () => {
  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-64 h-64 bg-primary/20 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent/20 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }} />
      </div>

      {/* Vertical lines pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,hsl(var(--border)/0.3)_1px,transparent_1px)] bg-[size:8rem_100%] pointer-events-none" style={{ maskImage: 'radial-gradient(ellipse 80% 50% at 50% 0%, black 70%, transparent 110%), linear-gradient(to bottom, black 0%, black 85%, transparent 100%)', maskComposite: 'intersect', WebkitMaskImage: 'radial-gradient(ellipse 80% 50% at 50% 0%, black 70%, transparent 110%), linear-gradient(to bottom, black 0%, black 85%, transparent 100%)', WebkitMaskComposite: 'source-in' }} />

      {/* Header */}
      <header className="border-b border-border/50 bg-card/30 backdrop-blur-xl sticky top-0 z-50 relative">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/">
            <img src="/logo.png" alt="Coddar Logo" className="h-11 w-auto" />
          </Link>
          <nav className="hidden md:flex gap-6">
            <Link to="/#servicos" className="text-muted-foreground hover:text-primary transition-colors">
              Serviços
            </Link>
            <Link to="/blog" className="text-primary transition-colors">
              Blog
            </Link>
            <Link to="/seguranca-digital" className="text-muted-foreground hover:text-primary transition-colors">
              Segurança Digital
            </Link>
            <Link to="/contato" className="text-muted-foreground hover:text-primary transition-colors">
              Contato
            </Link>
          </nav>
          <Button asChild variant="outline" className="border-primary/50 text-primary hover:bg-primary/10 hover:border-primary">
            <a href="https://folio.app.br/area-cliente">Área do Cliente</a>
          </Button>
        </div>
      </header>

      {/* Article */}
      <article className="py-12 md:py-20 px-4 relative">
        <div className="container mx-auto max-w-4xl relative z-10">
          <Button asChild variant="ghost" className="mb-8 gap-2 hover:gap-3 transition-all">
            <Link to="/blog">
              <ArrowLeft className="h-4 w-4" /> Voltar ao Blog
            </Link>
          </Button>

          <Card className="border-border/50 bg-card/50 backdrop-blur-sm p-8 md:p-12">
            {/* Featured Image */}
            <div className="aspect-video w-full rounded-lg mb-8 overflow-hidden">
              <BlogPostImage
                title="IA Generativa Revoluciona o Desenvolvimento"
                backgroundImage="/assets/blog-IA-Generativa-Revoluciona-Desenvolvimento-Software.webp"
                icon={<Sparkles className="w-full h-full" />}
              />
            </div>

            {/* Meta Info */}
            <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mb-6">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span>8 de Novembro, 2025</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                <span>9 min de leitura</span>
              </div>
              <div className="flex items-center gap-2">
                <User className="h-4 w-4" />
                <span>Equipe Coddar</span>
              </div>
            </div>

            {/* Title */}
            <h1 className="text-3xl md:text-5xl font-bold text-foreground mb-6">
              IA Generativa Revoluciona o Desenvolvimento de Software
            </h1>

            {/* Content */}
            <div className="prose prose-invert max-w-none">
              <p className="text-lg text-muted-foreground mb-6">
                A inteligência artificial generativa está transformando radicalmente a forma como desenvolvemos software. Ferramentas como ChatGPT, GitHub Copilot, Claude e outras estão se tornando parceiras indispensáveis dos desenvolvedores, aumentando produtividade e democratizando a programação.
              </p>

              <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">O Impacto da IA no Desenvolvimento</h2>
              <p className="text-muted-foreground mb-4">
                Segundo pesquisas recentes, desenvolvedores que usam assistentes de IA reportam:
              </p>
              <ul className="list-disc list-inside text-muted-foreground mb-6 space-y-2">
                <li>55% de aumento na velocidade de conclusão de tarefas</li>
                <li>Redução de 40% no tempo gasto com debugging</li>
                <li>Maior satisfação no trabalho e menos tarefas repetitivas</li>
                <li>Capacidade de aprender novas tecnologias mais rapidamente</li>
              </ul>

              <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Principais Ferramentas de IA para Desenvolvedores</h2>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">GitHub Copilot</h3>
              <p className="text-muted-foreground mb-4">
                Desenvolvido pela GitHub em parceria com OpenAI, o Copilot sugere código em tempo real diretamente no seu editor. Funciona como um "pair programmer" que entende contexto e oferece soluções inteligentes.
              </p>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">ChatGPT e GPT-4</h3>
              <p className="text-muted-foreground mb-4">
                Além de gerar código, o ChatGPT explica conceitos, debugga erros, otimiza algoritmos e até cria documentação. A versão GPT-4 tem capacidade ainda maior de raciocínio e compreensão de contexto.
              </p>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">Claude (Anthropic)</h3>
              <p className="text-muted-foreground mb-4">
                Conhecido por sua capacidade de lidar com contextos longos, o Claude é excelente para analisar bases de código extensas e fornecer refatorações complexas.
              </p>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">Cursor e Continue</h3>
              <p className="text-muted-foreground mb-4">
                Editores de código nativos com IA integrada, oferecendo experiência mais fluida que plugins tradicionais.
              </p>

              <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Casos de Uso Práticos</h2>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">1. Geração de Código Boilerplate</h3>
              <p className="text-muted-foreground mb-4">
                Crie rapidamente estruturas de projetos, configurações, testes unitários e código repetitivo, economizando horas de trabalho manual.
              </p>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">2. Debugging Inteligente</h3>
              <p className="text-muted-foreground mb-4">
                Cole uma mensagem de erro e obtenha não apenas a solução, mas também explicação do problema e melhores práticas para evitá-lo no futuro.
              </p>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">3. Refatoração de Código</h3>
              <p className="text-muted-foreground mb-4">
                Melhore código legado, aplique design patterns e otimize performance com sugestões contextualizadas.
              </p>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">4. Documentação Automática</h3>
              <p className="text-muted-foreground mb-4">
                Gere documentação técnica, comentários de código e READMEs completos automaticamente.
              </p>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">5. Aprendizado Acelerado</h3>
              <p className="text-muted-foreground mb-4">
                Aprenda novas linguagens, frameworks e conceitos com explicações personalizadas e exemplos práticos.
              </p>

              <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Melhores Práticas</h2>
              <ul className="list-disc list-inside text-muted-foreground mb-6 space-y-2">
                <li>Sempre revise o código gerado - IA pode cometer erros</li>
                <li>Use prompts claros e específicos para melhores resultados</li>
                <li>Combine IA com seu conhecimento técnico</li>
                <li>Mantenha-se atualizado sobre limitações e vieses</li>
                <li>Considere questões de segurança e privacidade</li>
              </ul>

              <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">O Futuro do Desenvolvimento</h2>
              <p className="text-muted-foreground mb-4">
                A IA não vai substituir desenvolvedores, mas desenvolvedores que usam IA vão substituir os que não usam. A tendência é que:
              </p>
              <ul className="list-disc list-inside text-muted-foreground mb-6 space-y-2">
                <li>Desenvolvimento se torne mais focado em arquitetura e lógica de negócio</li>
                <li>Barreiras de entrada para programação diminuam</li>
                <li>Produtividade aumente exponencialmente</li>
                <li>Qualidade de código melhore com análises automáticas</li>
              </ul>

              <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Conclusão</h2>
              <p className="text-muted-foreground mb-4">
                A IA generativa já é uma realidade no desenvolvimento de software. Empresas e desenvolvedores que adotam essas ferramentas ganham vantagem competitiva significativa. Na Coddar, utilizamos as mais recentes tecnologias de IA para entregar projetos com mais qualidade e agilidade.
              </p>
            </div>

            {/* CTA */}
            <div className="mt-12 p-6 bg-primary/10 border border-primary/20 rounded-lg">
              <h3 className="text-xl font-bold text-foreground mb-2">Quer desenvolver com IA?</h3>
              <p className="text-muted-foreground mb-4">
                Nossa equipe utiliza as mais avançadas ferramentas de IA para criar soluções inovadoras e eficientes.
              </p>
              <Button asChild>
                <Link to="/contato">Fale Conosco</Link>
              </Button>
            </div>
          </Card>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default BlogPost4;
